import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import type { WhatsAppSendRecipient } from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppRecipientPreview } from '../../models/MasterAdmin/whatsappCampaign';
import { browseWhatsAppSendRecipients } from '../../services/MasterAdmin/whatsappCampaignService';
import { recipientKey } from './whatsappPreview';
import { formatDisplayPhone } from './whatsappUi';

export type RecipientSourceTab = 'contacts' | 'users' | 'vendors';
export type PlatformFilter = 'all' | 'web' | 'ios' | 'android';
export type VendorKindFilter = 'all' | 'doctor' | 'ambulance';

const SOURCE_TABS: { id: RecipientSourceTab; label: string }[] = [
  { id: 'contacts', label: 'Saved contacts' },
  { id: 'users', label: 'Users' },
  { id: 'vendors', label: 'Vendors' },
];

const PLATFORM_OPTIONS: { id: PlatformFilter; label: string }[] = [
  { id: 'all', label: 'All platforms' },
  { id: 'web', label: 'Web' },
  { id: 'ios', label: 'iOS' },
  { id: 'android', label: 'Android' },
];

const VENDOR_KIND_OPTIONS: { id: VendorKindFilter; label: string }[] = [
  { id: 'all', label: 'All vendors' },
  { id: 'doctor', label: 'Doctors' },
  { id: 'ambulance', label: 'Ambulance' },
];

const inputClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800';

function toSendRecipient(r: WhatsAppRecipientPreview): WhatsAppSendRecipient | null {
  const cc = r.countryCode || '+91';
  const pn = r.phoneNumber || r.phone?.replace(/\D/g, '').slice(-10) || '';
  if (!pn) return null;
  const linkedUserId = (r as WhatsAppRecipientPreview & { linkedUserId?: string | null }).linkedUserId;
  const linkedVendorId = (r as WhatsAppRecipientPreview & { linkedVendorId?: string | null }).linkedVendorId;
  return {
    key: recipientKey(cc, pn),
    countryCode: cc,
    phoneNumber: pn,
    displayName: r.name,
    maskedPhone: r.maskedPhone,
    source: r.source,
    recipientType: r.recipientType,
    recipientId: linkedUserId || linkedVendorId || r.recipientId,
    platform: r.platform,
    vendorKind: r.vendorKind,
    email: r.email ?? null,
    city: r.city ?? null,
    tags: r.tags ?? [],
  };
}

function platformLabel(platform?: string | null) {
  if (!platform) return null;
  const p = platform.toLowerCase();
  if (p === 'ios') return 'iOS';
  if (p === 'android') return 'Android';
  if (p === 'web') return 'Web';
  return platform;
}

function kindLabel(r: WhatsAppRecipientPreview) {
  if (r.vendorKind === 'doctor-clinic') return 'Doctor';
  if (r.vendorKind === 'ambulance') return 'Ambulance';
  if (r.recipientType === 'user') return 'User';
  if (r.source === 'contact') return 'Contact';
  if (r.recipientType === 'vendor') return 'Vendor';
  return r.recipientType;
}

type Props = {
  recipients: WhatsAppSendRecipient[];
  onChange: (next: WhatsAppSendRecipient[]) => void;
  disabled?: boolean;
  /** When true, picking a row replaces the selection instead of multi-select. */
  singleSelect?: boolean;
  /** Limit which source tabs appear. Defaults to all. */
  allowedSources?: RecipientSourceTab[];
  title?: string;
};

const WhatsAppSendRecipientPicker: React.FC<Props> = ({
  recipients,
  onChange,
  disabled,
  singleSelect = false,
  allowedSources,
  title,
}) => {
  const tabs = useMemo(
    () =>
      allowedSources?.length
        ? SOURCE_TABS.filter((t) => allowedSources.includes(t.id))
        : SOURCE_TABS,
    [allowedSources]
  );
  const [source, setSource] = useState<RecipientSourceTab>(tabs[0]?.id || 'contacts');
  const [platform, setPlatform] = useState<PlatformFilter>('all');
  const [vendorKind, setVendorKind] = useState<VendorKindFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [browseRows, setBrowseRows] = useState<WhatsAppRecipientPreview[]>([]);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tabs.some((t) => t.id === source) && tabs[0]) setSource(tabs[0].id);
  }, [tabs, source]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const loadBrowse = useCallback(async () => {
    setLoading(true);
    try {
      const res = await browseWhatsAppSendRecipients({
        source,
        platform: source === 'contacts' ? 'all' : platform,
        vendorKind: source === 'vendors' ? vendorKind : 'all',
        q: debouncedSearch || undefined,
        limit: 80,
      });
      setBrowseRows(res.data || []);
      setBrowseTotal(res.meta?.total ?? res.data?.length ?? 0);
    } catch {
      setBrowseRows([]);
      setBrowseTotal(0);
    } finally {
      setLoading(false);
    }
  }, [source, platform, vendorKind, debouncedSearch]);

  useEffect(() => {
    void loadBrowse();
  }, [loadBrowse]);

  const selectedKeys = useMemo(() => new Set(recipients.map((r) => r.key)), [recipients]);

  const toggleRecipient = (row: WhatsAppRecipientPreview) => {
    const next = toSendRecipient(row);
    if (!next) return;
    if (singleSelect) {
      if (selectedKeys.has(next.key)) {
        onChange([]);
      } else {
        onChange([next]);
      }
      return;
    }
    if (selectedKeys.has(next.key)) {
      onChange(recipients.filter((r) => r.key !== next.key));
    } else {
      onChange([...recipients, next]);
    }
  };

  const selectableRows = useMemo(
    () => browseRows.map((r) => ({ row: r, send: toSendRecipient(r) })).filter((x) => x.send),
    [browseRows]
  );

  const allVisibleSelected =
    !singleSelect &&
    selectableRows.length > 0 &&
    selectableRows.every((x) => selectedKeys.has(x.send!.key));

  const toggleSelectAllVisible = () => {
    if (singleSelect) return;
    if (allVisibleSelected) {
      const visibleKeys = new Set(selectableRows.map((x) => x.send!.key));
      onChange(recipients.filter((r) => !visibleKeys.has(r.key)));
      return;
    }
    const merged = new Map(recipients.map((r) => [r.key, r]));
    for (const { send } of selectableRows) {
      if (send) merged.set(send.key, send);
    }
    onChange(Array.from(merged.values()));
  };

  const removeRecipient = (key: string) => {
    onChange(recipients.filter((r) => r.key !== key));
  };

  return (
    <div className="space-y-3">
      {title ? <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{title}</p> : null}
      <div className="flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            disabled={disabled}
            onClick={() => setSource(tab.id)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
              source === tab.id
                ? 'bg-white text-[#128C7E] shadow-sm dark:bg-gray-900'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {(source === 'users' || source === 'vendors') && (
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">Platform</p>
          <div className="flex flex-wrap gap-1.5">
            {PLATFORM_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                disabled={disabled}
                onClick={() => setPlatform(opt.id)}
                className={`rounded-full px-2.5 py-1 text-xs ${
                  platform === opt.id
                    ? 'bg-[#25D366]/15 font-medium text-[#128C7E]'
                    : 'border border-gray-200 text-gray-600 dark:border-gray-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {source === 'vendors' && (
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">Vendor type</p>
          <div className="flex flex-wrap gap-1.5">
            {VENDOR_KIND_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                disabled={disabled}
                onClick={() => setVendorKind(opt.id)}
                className={`rounded-full px-2.5 py-1 text-xs ${
                  vendorKind === opt.id
                    ? 'bg-[#25D366]/15 font-medium text-[#128C7E]'
                    : 'border border-gray-200 text-gray-600 dark:border-gray-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
          placeholder={
            source === 'contacts'
              ? 'Search saved contacts...'
              : source === 'users'
                ? 'Search users by name...'
                : 'Search vendors by name...'
          }
          className={`${inputClass} pl-9`}
        />
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        {!singleSelect ? (
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              disabled={disabled || !selectableRows.length}
              onChange={toggleSelectAllVisible}
              className="rounded border-gray-300 text-[#25D366] focus:ring-[#25D366]"
            />
            Select all shown ({selectableRows.length})
          </label>
        ) : (
          <span>Select one recipient</span>
        )}
        <span>{browseTotal} match{browseTotal === 1 ? '' : 'es'}</span>
      </div>

      <div className="max-h-52 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700">
        {loading ? (
          <p className="px-3 py-6 text-center text-sm text-gray-500">Loading recipients...</p>
        ) : selectableRows.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-gray-500">
            No recipients found. Try another filter or add contacts first.
          </p>
        ) : (
          selectableRows.map(({ row, send }) => {
            const checked = selectedKeys.has(send!.key);
            return (
              <label
                key={send!.key}
                className={`flex cursor-pointer items-start gap-3 border-b px-3 py-2.5 last:border-0 dark:border-gray-700 ${
                  checked ? 'bg-[#25D366]/5' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'
                }`}
              >
                <input
                  type={singleSelect ? 'radio' : 'checkbox'}
                  name={singleSelect ? 'campaign-single-recipient' : undefined}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggleRecipient(row)}
                  className="mt-0.5 rounded border-gray-300 text-[#25D366] focus:ring-[#25D366]"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium text-gray-900 dark:text-white">{row.name}</span>
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-gray-800">
                      {kindLabel(row)}
                    </span>
                    {platformLabel(row.platform) && (
                      <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                        {platformLabel(row.platform)}
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-[#8696a0]">
                    {formatDisplayPhone(row.countryCode || '+91', row.phoneNumber || row.phone || '')}
                  </span>
                </span>
              </label>
            );
          })
        )}
      </div>

      {recipients.length > 0 && (
        <div>
          <p className="mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
            Selected ({recipients.length})
          </p>
          <div className="flex max-h-28 flex-wrap gap-2 overflow-y-auto">
            {recipients.map((r) => (
              <span
                key={r.key}
                className="inline-flex max-w-full items-center gap-1 rounded-full bg-[#25D366]/10 px-2.5 py-1 text-xs text-[#128C7E]"
              >
                <span className="truncate">
                  {r.displayName}
                  {r.platform ? ` · ${platformLabel(r.platform)}` : ''}
                </span>
                <button type="button" disabled={disabled} onClick={() => removeRecipient(r.key)} aria-label="Remove">
                  <XMarkIcon className="h-3.5 w-3.5 shrink-0" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppSendRecipientPicker;
